<template>
    <div class="page-container">
        <!-- Alert -->
        <soft-alert v-if="alertTable.state" :color="alertTable.color" class="mb-3" dismissible>
            {{ alertTable.message }}
        </soft-alert>

        <!-- Page header -->
        <CrmPageHeader
            title="Consumo de IA"
            :subtitle="periodoTexto"
            icon="fas fa-microchip"
        >
            <soft-button variant="outline" color="secondary" size="sm" @click="getAiUsage" :loading="spinner">
                <i class="fas fa-sync-alt me-1"></i> Actualizar
            </soft-button>
        </CrmPageHeader>

        <!-- Filtros -->
        <div class="crm-card mb-4">
            <div class="crm-card__body">
                <div class="row g-3 align-items-end">
                    <div class="col-lg-2 col-sm-6">
                        <label class="filter-label">Desde</label>
                        <input type="date" class="form-control form-control-sm" v-model="filtros.desde" />
                    </div>
                    <div class="col-lg-2 col-sm-6">
                        <label class="filter-label">Hasta</label>
                        <input type="date" class="form-control form-control-sm" v-model="filtros.hasta" />
                    </div>
                    <div class="col-lg-3 col-sm-6">
                        <label class="filter-label">Usuario</label>
                        <v-select
                            v-model="usuarioSel"
                            :options="miembros"
                            :filterable="false"
                            :loading="loadingMiembros"
                            @search="buscarMiembros"
                            placeholder="Todos"
                            class="ai-select"
                        >
                            <template #no-options="{ search }">
                                <span class="text-sm text-secondary">{{ textoSinOpciones(search) }}</span>
                            </template>
                        </v-select>
                        <p v-if="miembrosTruncados" class="filter-hint">
                            Mostrando {{ miembros.length }} de {{ miembrosTotal }} — afina la búsqueda
                        </p>
                    </div>
                    <div class="col-lg-2 col-sm-6">
                        <label class="filter-label">TRM (USD → COP)</label>
                        <input
                            type="number"
                            min="1"
                            step="1"
                            class="form-control form-control-sm"
                            :value="trm"
                            @change="setTrm($event.target.value)"
                        />
                    </div>
                    <div class="col-lg-3">
                        <div class="d-flex gap-2">
                            <soft-button variant="gradient" size="sm" @click="aplicarFiltros" :loading="spinner">
                                <i class="fas fa-filter me-1"></i> Filtrar
                            </soft-button>
                            <soft-button variant="outline" color="secondary" size="sm" @click="limpiarFiltros">
                                Limpiar
                            </soft-button>
                        </div>
                    </div>
                </div>

                <!-- Rangos rápidos -->
                <div class="d-flex flex-wrap gap-2 mt-3">
                    <button
                        v-for="r in rangos"
                        :key="r.key"
                        type="button"
                        class="range-chip"
                        @click="aplicarRango(r.key)"
                    >
                        {{ r.label }}
                    </button>
                </div>
            </div>
        </div>

        <soft-spinner v-if="spinner" />

        <template v-else>
            <!-- Stats Cards -->
            <div class="row g-3 mb-4">
                <div class="col-xl-3 col-sm-6">
                    <div class="stat-card d-flex align-items-center gap-3">
                        <div class="stat-card__icon bg-gradient-primary">
                            <i class="fas fa-dollar-sign text-white"></i>
                        </div>
                        <div>
                            <p class="stat-card__label">Total del periodo</p>
                            <p class="stat-card__value">{{ formatUsd(totalUsd) }}</p>
                            <p class="stat-card__hint">{{ formatCop(totalUsd) }}</p>
                        </div>
                    </div>
                </div>
                <div class="col-xl-3 col-sm-6">
                    <div class="stat-card d-flex align-items-center gap-3">
                        <div class="stat-card__icon bg-gradient-success">
                            <i class="fas fa-comments text-white"></i>
                        </div>
                        <div>
                            <p class="stat-card__label">Por conversación</p>
                            <p class="stat-card__value">{{ formatUsd(costoPorConversacionUsd) }}</p>
                            <p class="stat-card__hint">{{ formatCop(costoPorConversacionUsd) }}</p>
                        </div>
                    </div>
                </div>
                <div class="col-xl-3 col-sm-6">
                    <div class="stat-card d-flex align-items-center gap-3">
                        <div class="stat-card__icon bg-gradient-info">
                            <i class="fas fa-comment-dots text-white"></i>
                        </div>
                        <div>
                            <p class="stat-card__label">Conversaciones</p>
                            <p class="stat-card__value">{{ formatNumber(totalConversaciones) }}</p>
                            <p class="stat-card__hint">Lo que el usuario ve como "una consulta"</p>
                        </div>
                    </div>
                </div>
                <div class="col-xl-3 col-sm-6">
                    <div class="stat-card d-flex align-items-center gap-3">
                        <div class="stat-card__icon bg-gradient-warning">
                            <i class="fas fa-bolt text-white"></i>
                        </div>
                        <div>
                            <p class="stat-card__label">Llamadas al proveedor</p>
                            <p class="stat-card__value">{{ formatNumber(totalLlamadas) }}</p>
                            <p class="stat-card__hint">Cada conversación son 2 o 3</p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Sin consumo -->
            <div v-if="sinConsumo" class="crm-card">
                <div class="crm-card__body text-center py-5">
                    <i class="fas fa-chart-pie text-secondary mb-3 d-block" style="font-size: 2.25rem; opacity: 0.35;"></i>
                    <p class="text-sm font-weight-bold mb-1">No hay consumo en este periodo</p>
                    <p class="text-xs text-secondary mb-0">
                        El histórico empieza el día del despliegue. Para el consumo anterior consulta
                        el panel de platform.claude.com.
                    </p>
                </div>
            </div>

            <div v-else class="row g-3">
                <!-- Desglose por función -->
                <div class="col-xl-4">
                    <div class="crm-card h-100">
                        <div class="crm-card__header">
                            <p style="font-size:.82rem;font-weight:600;color:#1E293B;margin:0">Gasto por función</p>
                            <p style="font-size:.72rem;color:#94A3B8;margin:0">Es lo que explica la factura</p>
                        </div>
                        <div class="crm-card__body">
                            <p v-if="!funcionesOrdenadas.length" class="text-xs text-secondary text-center mb-0 py-3">
                                Sin datos
                            </p>
                            <div v-for="f in funcionesOrdenadas" :key="f.feature + f.model" class="feature-row">
                                <div class="d-flex align-items-center justify-content-between mb-1">
                                    <div class="d-flex align-items-center gap-2">
                                        <i :class="featureIcon(f.feature)" class="feature-row__icon"></i>
                                        <div>
                                            <p class="feature-row__name">{{ featureLabel(f.feature) }}</p>
                                            <p class="feature-row__model">{{ f.model }}</p>
                                        </div>
                                    </div>
                                    <div class="text-end">
                                        <p class="feature-row__cost">{{ formatUsd(f.costo_usd) }}</p>
                                        <p class="feature-row__calls">{{ formatNumber(f.llamadas) }} llamadas</p>
                                    </div>
                                </div>
                                <div class="feature-bar">
                                    <div
                                        class="feature-bar__fill"
                                        :class="`bg-gradient-${featureColor(f.feature)}`"
                                        :style="{ width: f.porcentaje.toFixed(1) + '%' }"
                                    ></div>
                                </div>
                                <p class="feature-row__pct">{{ f.porcentaje.toFixed(1) }}% del total</p>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Tabla de usuarios -->
                <div class="col-xl-8">
                    <div class="crm-card h-100">
                        <div class="crm-card__header d-flex align-items-center justify-content-between">
                            <div>
                                <p style="font-size:.82rem;font-weight:600;color:#1E293B;margin:0">Consumo por usuario</p>
                                <p style="font-size:.72rem;color:#94A3B8;margin:0">De mayor a menor gasto</p>
                            </div>
                            <span class="badge bg-light" style="font-size:.75rem;padding:6px 12px">
                                {{ usuariosOrdenados.length }} usuarios
                            </span>
                        </div>
                        <div class="crm-card__body">
                            <div class="table-responsive p-0">
                                <table class="table align-items-center mb-0">
                                    <thead>
                                        <tr>
                                            <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Usuario</th>
                                            <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2 text-center">Conversaciones</th>
                                            <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2 text-center">Llamadas</th>
                                            <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2 text-center">Latencia</th>
                                            <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2 text-end">Costo</th>
                                            <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2 text-center">Tokens</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-if="!usuariosOrdenados.length">
                                            <td colspan="6" class="text-center py-5">
                                                <i class="fas fa-inbox text-secondary mb-2 d-block" style="font-size: 2rem; opacity: 0.4;"></i>
                                                <span class="text-secondary text-sm">Sin consumo en el periodo</span>
                                            </td>
                                        </tr>
                                        <template v-for="u in usuariosOrdenados" :key="u.user_id">
                                            <tr>
                                                <td>
                                                    <div class="d-flex px-2 py-1 flex-column">
                                                        <p class="text-sm font-weight-bold mb-0 text-dark">{{ u.usuario }}</p>
                                                        <p class="text-xxs text-secondary mb-0">{{ u.email }}</p>
                                                    </div>
                                                </td>
                                                <td class="text-center">
                                                    <span class="text-sm font-weight-bold">{{ formatNumber(u.conversaciones) }}</span>
                                                </td>
                                                <td class="text-center">
                                                    <span class="text-sm text-secondary">{{ formatNumber(u.llamadas) }}</span>
                                                </td>
                                                <td class="text-center">
                                                    <span class="text-xs text-secondary">{{ formatMs(u.ms_promedio) }}</span>
                                                </td>
                                                <td class="text-end">
                                                    <p class="text-sm font-weight-bold mb-0">{{ formatUsd(u.costo_usd) }}</p>
                                                    <p class="text-xxs text-secondary mb-0">{{ formatCop(u.costo_usd) }}</p>
                                                    <div class="user-bar mt-1">
                                                        <div
                                                            class="user-bar__fill"
                                                            :style="{ width: porcentajeDeTotal(u.costo_usd).toFixed(1) + '%' }"
                                                        ></div>
                                                    </div>
                                                </td>
                                                <td class="text-center">
                                                    <a href="#" class="table-action-link" title="Ver tokens"
                                                        @click.prevent="toggleTokens(u.user_id)">
                                                        <i :class="expandido === u.user_id ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
                                                    </a>
                                                </td>
                                            </tr>
                                            <tr v-if="expandido === u.user_id">
                                                <td colspan="6" class="bg-light-subtle">
                                                    <div class="token-detail">
                                                        <div class="token-chip">
                                                            <span class="token-chip__label">Entrada</span>
                                                            <span class="token-chip__value">{{ formatNumber(u.tokens?.entrada) }}</span>
                                                        </div>
                                                        <div class="token-chip">
                                                            <span class="token-chip__label">Escritura de caché</span>
                                                            <span class="token-chip__value">{{ formatNumber(u.tokens?.cache_write) }}</span>
                                                        </div>
                                                        <div class="token-chip">
                                                            <span class="token-chip__label">Lectura de caché <em>(barato)</em></span>
                                                            <span class="token-chip__value">{{ formatNumber(u.tokens?.cache_read) }}</span>
                                                        </div>
                                                        <div class="token-chip">
                                                            <span class="token-chip__label">Salida</span>
                                                            <span class="token-chip__value">{{ formatNumber(u.tokens?.salida) }}</span>
                                                        </div>
                                                    </div>
                                                    <p class="token-note">
                                                        <i class="fas fa-info-circle me-1"></i>
                                                        Los cuatro contadores no se suman: tienen precios distintos.
                                                        El costo real es el de la columna <strong>Costo</strong>.
                                                    </p>
                                                </td>
                                            </tr>
                                        </template>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import { computed, ref, watch } from "vue";
import SoftButton from "@/components/SoftButton.vue";
import SoftSpinner from "@/components/SoftSpinner.vue";
import SoftAlert from "@/components/SoftAlert.vue";
import CrmPageHeader from "@/components/CrmPageHeader.vue";
import { useAiUsage } from "./useAiUsage.js";

export default {
    name: "AiUsageReport",
    components: {
        SoftButton,
        SoftSpinner,
        SoftAlert,
        CrmPageHeader,
    },
    setup() {
        const {
            spinner, alertTable, filtros, trm, setTrm,
            periodo, totalUsd, costoPorConversacionUsd,
            miembros, miembrosTotal, miembrosTruncados, loadingMiembros,
            totalLlamadas, totalConversaciones,
            funcionesOrdenadas, usuariosOrdenados, sinConsumo,
            getAiUsage, buscarMiembros, aplicarFiltros, limpiarFiltros, aplicarRango,
            formatUsd, formatCop, formatNumber, formatMs, formatFecha,
            featureLabel, featureIcon, featureColor, porcentajeDeTotal,
        } = useAiUsage();

        getAiUsage();

        // v-select guarda el objeto completo para no perder la etiqueta cuando
        // cambian las opciones; el filtro solo necesita el id.
        const usuarioSel = ref(null);
        watch(usuarioSel, (opcion) => {
            filtros.value.user_id = opcion?.value || '';
        });
        watch(() => filtros.value.user_id, (id) => {
            if (!id) usuarioSel.value = null;
        });

        const textoSinOpciones = (search) =>
            !search || search.trim().length < 3
                ? 'Escribe al menos 3 letras…'
                : 'Sin resultados';

        const expandido = ref(null);
        const toggleTokens = (userId) => {
            expandido.value = expandido.value === userId ? null : userId;
        };

        const rangos = [
            { key: 'mes', label: 'Mes actual' },
            { key: 'mesAnterior', label: 'Mes anterior' },
            { key: '7dias', label: 'Últimos 7 días' },
            { key: '30dias', label: 'Últimos 30 días' },
            { key: 'anio', label: 'Este año' },
        ];

        const periodoTexto = computed(() => {
            if (!periodo.value) return 'Gasto de las funciones con IA del CRM';
            return `Del ${formatFecha(periodo.value.desde)} al ${formatFecha(periodo.value.hasta)}`;
        });

        return {
            spinner, alertTable, filtros, trm, setTrm,
            periodo, periodoTexto, totalUsd, costoPorConversacionUsd,
            miembros, miembrosTotal, miembrosTruncados, loadingMiembros, usuarioSel,
            totalLlamadas, totalConversaciones,
            funcionesOrdenadas, usuariosOrdenados, sinConsumo,
            getAiUsage, buscarMiembros, aplicarFiltros, limpiarFiltros, aplicarRango,
            formatUsd, formatCop, formatNumber, formatMs, formatFecha,
            featureLabel, featureIcon, featureColor, porcentajeDeTotal,
            expandido, toggleTokens, rangos, textoSinOpciones,
        };
    },
};
</script>

<style lang="scss" scoped>
.filter-label {
    font-size: 0.7rem;
    font-weight: 600;
    color: #64748B;
    margin-bottom: 4px;
    display: block;
}

.filter-hint {
    font-size: 0.66rem;
    color: #94A3B8;
    margin: 4px 0 0;
}

// vue-select a la altura de los .form-control-sm de la barra de filtros
.ai-select {
    :deep(.vs__dropdown-toggle) {
        border: 1px solid #d2d6da;
        border-radius: 0.5rem;
        padding: 0;
        min-height: 33px;
        background: #fff;
    }

    &.vs--open :deep(.vs__dropdown-toggle),
    :deep(.vs__dropdown-toggle:focus-within) {
        border-color: #b8c4d4;
        box-shadow: 0 0 0 2px rgba(94, 114, 228, 0.1);
    }

    :deep(.vs__search),
    :deep(.vs__selected),
    :deep(.vs__dropdown-menu) {
        font-size: 0.8rem;
        color: #495057;
    }

    :deep(.vs__selected) {
        margin: 2px 2px 2px 6px;
    }

    :deep(.vs__actions) {
        padding-top: 2px;
    }
}

.stat-card__hint {
    font-size: 0.68rem;
    color: #94A3B8;
    margin: 0;
}

.range-chip {
    border: 1px solid #E2E8F0;
    background: #fff;
    border-radius: 999px;
    padding: 4px 12px;
    font-size: 0.72rem;
    color: #475569;
    transition: all 0.15s ease;

    &:hover {
        background: #F1F5F9;
        color: #1E293B;
    }
}

.feature-row {
    padding: 12px 0;

    &:not(:last-child) {
        border-bottom: 1px solid #F1F5F9;
    }

    &__icon {
        color: #64748B;
        font-size: 0.85rem;
        width: 20px;
        text-align: center;
    }

    &__name {
        font-size: 0.8rem;
        font-weight: 600;
        color: #1E293B;
        margin: 0;
    }

    &__model {
        font-size: 0.66rem;
        color: #94A3B8;
        margin: 0;
    }

    &__cost {
        font-size: 0.8rem;
        font-weight: 700;
        color: #1E293B;
        margin: 0;
    }

    &__calls {
        font-size: 0.66rem;
        color: #94A3B8;
        margin: 0;
    }

    &__pct {
        font-size: 0.66rem;
        color: #94A3B8;
        margin: 4px 0 0;
    }
}

.feature-bar {
    height: 6px;
    border-radius: 999px;
    background: #F1F5F9;
    overflow: hidden;

    &__fill {
        height: 100%;
        border-radius: 999px;
        transition: width 0.3s ease;
    }
}

.user-bar {
    height: 4px;
    border-radius: 999px;
    background: #F1F5F9;
    overflow: hidden;

    &__fill {
        height: 100%;
        border-radius: 999px;
        background: linear-gradient(310deg, #7928CA, #FF0080);
    }
}

.token-detail {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    padding: 12px 16px 4px;
}

.token-chip {
    background: #fff;
    border: 1px solid #E2E8F0;
    border-radius: 0.5rem;
    padding: 8px 14px;
    min-width: 140px;

    &__label {
        display: block;
        font-size: 0.66rem;
        color: #94A3B8;
    }

    &__value {
        display: block;
        font-size: 0.85rem;
        font-weight: 700;
        color: #1E293B;
    }
}

.token-note {
    font-size: 0.66rem;
    color: #94A3B8;
    margin: 0;
    padding: 0 16px 12px;
}

.table-action-link {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border-radius: 0.375rem;
    color: #8392AB;
    transition: all 0.15s ease;
    text-decoration: none;

    &:hover {
        background-color: #f0f2f5;
        color: #344767;
    }
}
</style>
